import type { IntlShape } from 'react-intl';

import { ErrorKeySet, type ValidationError, type Validator } from '../../../types/field-validation';
import type { FormFieldValidation } from '../../../types/form/form-fields';
import type { IsoDate } from '../../../types/form/form-fields/field-values';
import { getFailedRequiredValidationError, getFailedValidationError } from '../utils';
import { messages } from './messages';

export const getDateValidationErrors = (
	validation: FormFieldValidation,
	fieldLabel: string,
	intl?: IntlShape,
	date?: IsoDate,
): ReturnType<Validator> => {
	if (!date) {
		if (validation.required) {
			return [getFailedRequiredValidationError(fieldLabel, intl)];
		}
		return [];
	}

	// Date values to be reused throughout the validation logic
	const minDate = validation.minimumDate ? new Date(validation.minimumDate) : undefined;
	const maxDate = validation.maximumDate ? new Date(validation.maximumDate) : undefined;
	const enteredDate = new Date(date);

	// Boolean values for validation logic
	const hasExactDateRequirements =
		validation.minimumDate && validation.minimumDate === validation.maximumDate;
	const hasBetweenDateRequirements = !!validation.minimumDate && !!validation.maximumDate;

	const validationErrors: ValidationError[] = [];

	if (hasExactDateRequirements && minDate) {
		if (enteredDate.valueOf() !== minDate.valueOf()) {
			validationErrors.push(
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.exactDate, { date: minDate }),
				),
			);
		} // No need to compare with both max and min again
	} else if (hasBetweenDateRequirements && minDate && maxDate) {
		// Between dates scenario
		if (enteredDate < minDate || enteredDate > maxDate) {
			validationErrors.push(
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.betweenDate, { minimum: minDate, maximum: maxDate }),
				),
			);
		}
	} else {
		if (validation.minimumDate && minDate) {
			if (enteredDate < minDate) {
				validationErrors.push(
					getFailedValidationError(
						ErrorKeySet.INVALID,
						intl?.formatMessage(messages.minimumDate, { minimum: minDate }),
					),
				);
			}
		}
		if (validation.maximumDate && maxDate) {
			if (enteredDate > maxDate) {
				validationErrors.push(
					getFailedValidationError(
						ErrorKeySet.INVALID,
						intl?.formatMessage(messages.maximumDate, { maximum: maxDate }),
					),
				);
			}
		}
	}
	return validationErrors;
};
